import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
  },
  pagePortrait: {
    padding: 30,
    fontSize: 9,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#666',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 14,
    marginBottom: 8,
  },
  table: {
    width: '100%',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 22,
    alignItems: 'center',
  },
  tableRowHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#1F2937',
    backgroundColor: '#F3F4F6',
    minHeight: 24,
    alignItems: 'center',
    fontWeight: 'bold',
  },
  cell: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    flex: 1,
  },
  cellId: { width: 28 },
  cellClient: { width: 90 },
  cellContact: { width: 110 },
  cellPaiement: { width: 42 },
  cellVoyageurs: { width: 68 },
  cellMontant: { width: 64 },
  cellDate: { width: 72 },
  // Liste participants
  cellResa: { width: 36 },
  cellContactShort: { width: 80 },
  cellType: { width: 44 },
  cellNom: { width: 90 },
  cellPrenom: { width: 90 },
  cellChambre: { width: 40 },
  participantRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 20,
    alignItems: 'center',
  },
  footerNote: {
    marginTop: 12,
    fontSize: 8,
    color: '#9CA3AF',
  },
});

export interface ReservationRow {
  id: number;
  client: string;
  email: string;
  phone: string;
  statut: string;
  paiement: string;
  avance: string;
  paye: string;
  adultes: number;
  enfants: number;
  bebes: number;
  total: string;
  date: string;
  formule: string;
  typeReservation: string;
}

export interface BookingParticipant {
  bookingId: number;
  clientName: string;
  personnes: Array<{ type: string; nom: string; prenom: string; chambre?: number }>;
  bebes: Array<{ nom: string; prenom: string; chambre?: number }>;
}

interface ReservationsListPDFProps {
  packageTitle: string;
  packageId: string;
  rows: ReservationRow[];
  generatedAt: string;
  participantsByBooking?: BookingParticipant[];
}

function buildParticipantRows(participantsByBooking: BookingParticipant[]): Array<{ resaId: number; clientName: string; type: string; nom: string; prenom: string; chambre: string }> {
  const out: Array<{ resaId: number; clientName: string; type: string; nom: string; prenom: string; chambre: string }> = [];
  participantsByBooking.forEach((b) => {
    b.personnes.forEach((p, i) => {
      const typeLabel = (p.type === 'adulte' ? 'Adulte' : 'Enfant') + (i + 1);
      out.push({
        resaId: b.bookingId,
        clientName: b.clientName,
        type: typeLabel,
        nom: p.nom || '—',
        prenom: p.prenom || '—',
        chambre: p.chambre != null ? String(p.chambre) : '—',
      });
    });
    b.bebes.forEach((p, i) => {
      out.push({
        resaId: b.bookingId,
        clientName: b.clientName,
        type: `Bébé ${i + 1}`,
        nom: p.nom || '—',
        prenom: p.prenom || '—',
        chambre: p.chambre != null ? String(p.chambre) : '—',
      });
    });
  });
  return out;
}

const ReservationsListPDF: React.FC<ReservationsListPDFProps> = ({
  packageTitle,
  packageId,
  rows,
  generatedAt,
  participantsByBooking = [],
}) => {
  const participantRows = participantsByBooking.length > 0 ? buildParticipantRows(participantsByBooking) : [];
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text style={styles.title}>Liste des réservations</Text>
        <Text style={styles.subtitle}>
          {packageTitle} (Package #{packageId}) — Généré le {generatedAt}
        </Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableRowHeader]}>
            <Text style={[styles.cell, styles.cellId]}>Id</Text>
            <Text style={[styles.cell, styles.cellClient]}>Client</Text>
            <Text style={[styles.cell, styles.cellContact]}>Contact</Text>
            <Text style={[styles.cell, styles.cellPaiement]}>Paiement</Text>
            <Text style={[styles.cell, styles.cellVoyageurs]}>Voyageurs</Text>
            <Text style={[styles.cell, styles.cellMontant]}>Prix total</Text>
            <Text style={[styles.cell, styles.cellDate]}>Date</Text>
          </View>
          {rows.map((row) => (
            <View key={row.id} style={styles.tableRow}>
              <Text style={[styles.cell, styles.cellId]}>{row.id}</Text>
              <Text style={[styles.cell, styles.cellClient]}>{row.client}</Text>
              <Text style={[styles.cell, styles.cellContact]} numberOfLines={1}>
                {row.email || '—'} / {row.phone || '—'}
              </Text>
              <Text style={[styles.cell, styles.cellPaiement]}>{row.paiement}</Text>
              <Text style={[styles.cell, styles.cellVoyageurs]}>
                {(row.adultes + row.enfants + row.bebes)} voy. ({row.adultes} ad. {row.enfants} enf. {row.bebes} béb.)
              </Text>
              <Text style={[styles.cell, styles.cellMontant]}>{row.total}</Text>
              <Text style={[styles.cell, styles.cellDate]}>{row.date}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.footerNote}>
          {rows.length} réservation{rows.length !== 1 ? 's' : ''}
        </Text>
      </Page>

      {participantRows.length > 0 && (
        <Page size="A4" orientation="portrait" style={styles.pagePortrait}>
          <Text style={styles.title}>Liste des participants — Recensement</Text>
          <Text style={styles.subtitle}>
            {packageTitle} (Package #{packageId}) — Généré le {generatedAt}
          </Text>
          <View style={styles.table}>
            <View style={[styles.participantRow, styles.tableRowHeader]}>
              <Text style={[styles.cell, styles.cellResa]}>Résa</Text>
              <Text style={[styles.cell, styles.cellContactShort]}>Contact</Text>
              <Text style={[styles.cell, styles.cellType]}>Type</Text>
              <Text style={[styles.cell, styles.cellNom]}>Nom</Text>
              <Text style={[styles.cell, styles.cellPrenom]}>Prénom</Text>
              <Text style={[styles.cell, styles.cellChambre]}>Ch.</Text>
            </View>
            {participantRows.map((p, idx) => (
              <View key={idx} style={styles.participantRow}>
                <Text style={[styles.cell, styles.cellResa]}>{p.resaId}</Text>
                <Text style={[styles.cell, styles.cellContactShort]} numberOfLines={1}>{p.clientName}</Text>
                <Text style={[styles.cell, styles.cellType]}>{p.type}</Text>
                <Text style={[styles.cell, styles.cellNom]}>{p.nom}</Text>
                <Text style={[styles.cell, styles.cellPrenom]}>{p.prenom}</Text>
                <Text style={[styles.cell, styles.cellChambre]}>{p.chambre}</Text>
              </View>
            ))}
          </View>
          <Text style={styles.footerNote}>
            {participantRows.length} participant{participantRows.length !== 1 ? 's' : ''} — À cocher lors du recensement
          </Text>
        </Page>
      )}
    </Document>
  );
};

export default ReservationsListPDF;
